/* eslint-disable react/no-unescaped-entities */
import Link from 'next/link'
import { useRouter } from 'next/router'
import react from 'react'

interface FacebookConnectedAppProps {
  id?: string
  facebookAdAccountId?: string
  isConnected?: boolean
  sessionId?: string
  oauthCallbackBaseUrl?: string
}

const FacebookConnectedApp = (props: FacebookConnectedAppProps) => {
  const router = useRouter()

  const [facebookInfoHidden, setFacebookInfoHidden] = react.useState(true)
  const [id, setId] = react.useState(props.id)
  const [facebookAdAccountId, setFacebookAdAccountId] = react.useState(props.facebookAdAccountId)
  const [saved, setSaved] = react.useState(false)
  const [isConnected, setIsConnected] = react.useState(props.isConnected)
  const [error, setError] = react.useState('')

	async function handleSaveConfigData(data: {[key: string]: any}) {
    // Save data
		const resp = await fetch(`/api/facebook/data`, {
			method: 'post',
			headers: {
				Session: props.sessionId!,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({id, ...data})
		})

    // Handle errors
    if (!resp.ok) {
      console.error('Failed to save config data', resp)
      setError('Error saving data')
      return
    }

    // Parse response
    const json = await resp.json()
    console.info('saved config data', json)
    setId(json.id)

    // Provide user feedback
    setSaved(true)

    setTimeout(() => {
      setSaved(false)
    }, 4000)

    setError('')

		return json.success
	}

  async function handleDeleteConnection() {
    const resp = await fetch(`/api/facebook/data`, {
      method: 'delete',
      headers: {
				Session: props.sessionId!,
      }
    })

    if (resp.ok) {
      setIsConnected(false)
    } else {
      console.error('error deleting connection', await resp.text())
      setError('Error deleting connection')
    }
  }

  const handleExtractUrl = (url: string) => {
    const parsed = new URL(url)
    const acctId = parsed.searchParams.get('act')
    setFacebookAdAccountId(acctId!)
  }

  react.useEffect(() => {
    console.log('updating state', props)
    setId(props.id)
    setFacebookAdAccountId(props.facebookAdAccountId)
    setIsConnected(props.isConnected)
  }, [props.id, props.facebookAdAccountId, props.isConnected])

  return (
     <div
      className='border-b border-gray-300 mb-5 bg-slate-200 p-8'
     >
      <h2 className="text-2xl text-slate-500">
        <span className="text-blue-700 font-extrabold">Facebook Ads</span><button type="button" className='underline text-blue-500 text-sm ml-2' onClick={() => setFacebookInfoHidden(!facebookInfoHidden)}>(help)</button>
      </h2>
      <div className={`${facebookInfoHidden ? 'hidden' : 'block'} bg-slate-100 p-8 my-5`}>
        <h3>Instructions</h3>
        <p className='text-sm mt-0'>Your Ad Account ID can be found in several different places on your Ads Manager dashboard</p>
        <img
          src='/assets/images/meta_ad_account_id_location.png'
          alt="Location of Facebook Ads Account ID"
        />
        <div className='mt-8'>
          Example
          <span className="block text-xs">
            https://adsmanager.facebook.com/adsmanager/manage/accounts?act=<span className="bg-amber-200">YOUR_AD_ACCOUNT_ID</span>&selected_campaign_ids=120202830446050089&selected_adset_ids=120202830446070089&selected_ad_ids=120202830446120089&breakdown_regrouping=true
          </span>
        </div>
        <h3 className="mt-6">
          Use the Tool
        </h3>
        <div className='mt-0'>
          <label className='text-sm block mb-2' htmlFor='fb-ad-parse-tool'>
            Copy and paste your resolved <Link href="https://adsmanager.facebook.com/adsmanager/manage/accounts" target="_blank">Ad Manager Url</Link> below 
            to automatically extract your Ad Account Id.
          </label>
          <input 
            id='fb-ad-parse-tool' 
            type="text" 
            className="border border-gray-300 rounded p-2 w-full" 
            onChange={(e) => handleExtractUrl(e.target.value)}
            placeholder='https://adsmanager.facebook.com/adsmanager/manage/accounts?act=YOUR_AD_ACCOUNT_ID&selected_campaign_ids=120202830446050089&selected_adset_ids=120202830446070089&selected_ad_ids=120202830446120089&breakdown_regrouping=true'
          />
        </div>
      </div>
      <form className='mt-8'>
          <label htmlFor="facebook-ad-account-id" className='block font-bold'>Ad Account ID</label>
          <div className="mt-0 mb-2 text-sm italic">
            Locate your Ad Account ID in the <a href="https://adsmanager.facebook.com/adsmanager/manage/accounts" target="_blank">Facebook ads manager dashboard URL </a>
          </div>
          <div className='flex flex-col md:flex-row align-middle m-0'>
            <input id="facebook-ad-account-id" 
              className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="text"
              placeholder="165354544531830"
              value={facebookAdAccountId}
              onChange={(e) => setFacebookAdAccountId(e.target.value)}
            />
              
            <button 
                  className="bg-blue-500 hover:bg-blue-700 text-white mt-4 md:mt-0 h-8 md:h-10 font-bold md:ml-5 px-4 rounded"
              onClick={() => handleSaveConfigData({facebookAdAccountId})}
            >
              { saved ? ' ✔️ ' : 'Save'}
            </button>
          </div>
      </form>

      <h2 className="mt-5 font-bold">Connect to Facebook</h2>
      <p className='text-sm mt-0 italic'>
        Connecting utilizes a Facebook <Link href="https://developers.facebook.com/docs/facebook-login/guides/access-tokens/get-long-lived/" target="_blank">long-lived 
        access token</Link> to access your Facebook Ads data.  This token lasts about 60 days, after which you will receive a notification email
        to reconnect.
      </p>
      { !isConnected &&
          <div className='text-med'>
            <a 
              className='inline-block border-2 border-[#1877F2] bg-white hover:bg-slate-100 hover:drop-shadow-xl text-[#1877F2] font-bold py-3 px-4 rounded mt-4'
              href={`https://www.facebook.com/v20.0/dialog/oauth?client_id=1480928246004898&redirect_uri=${props.oauthCallbackBaseUrl}/oauth-callback/facebook&scope=ads_read`}
            >
              <img className="inline" src={`${router.basePath}/assets/images/facebook_2x.png`}/> 
              <span className='ml-3'>Continue with Facebook</span>
            </a>
          </div>
        }
        { isConnected &&
          <div className='text-med'>
            Done ✅ <br />
            <a 
              href={`https://www.facebook.com/v20.0/dialog/oauth?client_id=1480928246004898&redirect_uri=${props.oauthCallbackBaseUrl}/oauth-callback/facebook&scope=ads_read`}
            >
              Reauthenticate</a>

            <button 
              className=" text-red-800 hover:underline mt-4 px-4 rounded"
              onClick={() => handleDeleteConnection()}>Delete Connection
            </button> 
            <div className='block text-red-600'>{ error }</div>
          </div>
        }
    </div>    
  )
}

export default FacebookConnectedApp